import {
  checkIfStringHasSpecialChar,
  compareStringsCaseDiacriticInsensitive,
} from "@/helpers/stringUtility";
import { useGetError } from "@/hooks";
import { approvalWorkflowType } from "@/propTypes";
import {
  useGetAllNotificationGroupsQuery,
  useGetApprovalWorkflowsQuery,
  useGetProjectRolesQuery,
} from "@/services";
import { Form, Input, Select } from "antd";
import _ from "lodash";
import { array, func } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { StyledTag } from "../styled";
import { validationCheck } from "./helpers";

const ApprovalWorkflowEditor = ({
  approvalWorkflow,
  setIsValidForm,
  onSetApprovalWorkFlow,
  projectTypes,
}) => {
  const intl = useIntl();

  const [isDuplicateName, setIsDuplicateName] = useState(false);
  const [invalidProjectTypes, setInvalidProjectTypes] = useState({});
  const [hasInvalidProjectTypes, setHasInvalidProjectTypes] = useState(false);
  const [templateRolesOptions, setTemplateRolesOptions] = useState();

  const { data: approvalWorkflows, isError } = useGetApprovalWorkflowsQuery();
  const { data: notificationGroups } = useGetAllNotificationGroupsQuery();
  const { data: projectRoles } = useGetProjectRolesQuery();

  useGetError({ isError, translationId: "approvalWorkFlows.title" });

  useEffect(() => {
    const projectTypeIds =
      approvalWorkflows
        ?.filter((x) => x.id !== approvalWorkflow?.id)
        ?.map(({ projectTypeIds }) => projectTypeIds) || [];

    const usedProjectTypes = _.keyBy(
      _.uniq(projectTypeIds.flat().filter((x) => !!x))
    );

    setInvalidProjectTypes(usedProjectTypes);
  }, [approvalWorkflow?.id, approvalWorkflows]);

  useEffect(() => {
    if (!projectRoles?.length) return;

    const options = projectRoles.map(({ id, name }) => ({
      label: name,
      value: id,
    }));

    setTemplateRolesOptions(options || []);
  }, [projectRoles]);

  useEffect(() => {
    setIsValidForm(
      validationCheck(approvalWorkflow, isDuplicateName, hasInvalidProjectTypes)
    );
  }, [
    approvalWorkflow,
    isDuplicateName,
    setIsValidForm,
    hasInvalidProjectTypes,
  ]);

  const setValue = (value) => {
    onSetApprovalWorkFlow({
      ...approvalWorkflow,
      ...value,
    });
  };

  const setName = (name) => {
    if (name.startsWith(" ") || checkIfStringHasSpecialChar(name)) return;

    setValue({ name });

    const duplicateCheck = approvalWorkflows
      ?.filter(({ id }) => id !== approvalWorkflow.id)
      .some((workflow) =>
        compareStringsCaseDiacriticInsensitive(workflow?.name, name)
      );

    setIsDuplicateName(duplicateCheck);
  };

  const setApprovers = (value) => {
    setValue({
      approvers: [
        {
          secondaryId: value,
          name: projectRoles.find(({ id }) => id === value).name,
        },
      ],
    });
  };

  const setProjectTypes = (values) => {
    const hasInvalidProjectType = values.some(
      (selectedProjectType) => !!invalidProjectTypes[selectedProjectType]
    );
    setValue({ projectTypeIds: values });
    setHasInvalidProjectTypes(hasInvalidProjectType);
  };

  return (
    <>
      <Form.Item
        data-test-id="approval-work-flow-editor-name-label"
        label={intl.formatMessage({
          id: "approvalWorkFlows.nameLabel",
        })}
      >
        <Input
          data-test-id="approval-work-flow-editor-name-input"
          value={approvalWorkflow?.name}
          onChange={({ target: { value } }) => setName(value)}
        />
        {isDuplicateName && (
          <StyledTag data-test-id="approval-work-flow-editor-dupname-error-label">
            {intl.formatMessage({ id: "approvalWorkFlows.dupNameError" })}
          </StyledTag>
        )}
      </Form.Item>
      <Form.Item
        data-test-id="approval-work-flow-editor-project-types-label"
        label={intl.formatMessage({
          id: "approvalWorkFlows.projectTypesLabel",
        })}
      >
        <Select
          data-test-id="approval-work-flow-editor-project-types-select"
          placeholder={intl.formatMessage({
            id: "approvalWorkFlows.selectProjectTypesPlaceholder",
          })}
          value={approvalWorkflow?.projectTypeIds}
          onChange={(value) => setProjectTypes(value)}
          mode="multiple"
          options={projectTypes}
        />
        {hasInvalidProjectTypes && (
          <StyledTag data-test-id="approval-work-flow-editor-dup-project-type-error-label">
            {intl.formatMessage({
              id: "approvalWorkFlows.dupProjectTypeError",
            })}
          </StyledTag>
        )}
      </Form.Item>
      {!approvalWorkflow?.id && (
        <Form.Item
          data-test-id="approval-work-flow-editor-approvers-label"
          label={intl.formatMessage({
            id: "approvalWorkFlows.approverLabel",
          })}
        >
          <Select
            data-test-id="approval-work-flow-editor-aprrovers-select"
            placeholder={intl.formatMessage({
              id: "approvalWorkFlows.selectApproverPlaceholder",
            })}
            value={approvalWorkflow?.approvers?.[0]?.secondaryId}
            onChange={(value) => setApprovers(value)}
            options={templateRolesOptions}
            filterOption={(input, option) => {
              if (!input?.length) return true;
              if (!option.label) return false;
              return (
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              );
            }}
            showSearch={true}
          />
        </Form.Item>
      )}
      <Form.Item
        label={intl.formatMessage({
          id: "notificationGroups.pageTitle",
        })}
      >
        <Select
          data-test-id="edit-approval-workflow-notification-groups"
          mode="multiple"
          value={approvalWorkflow?.notificationGroups}
          onChange={(id) => setValue({ notificationGroups: id })}
        >
          {notificationGroups?.map(({ id, name }) => (
            <Select.Option key={id}>{name}</Select.Option>
          ))}
        </Select>
      </Form.Item>
    </>
  );
};

ApprovalWorkflowEditor.propTypes = {
  approvalWorkflow: approvalWorkflowType,
  projectTypes: array,
  setIsValidForm: func.isRequired,
  onSetApprovalWorkFlow: func.isRequired,
};

export default ApprovalWorkflowEditor;
